import React from 'react'
import type { PageViewMode } from '@/components/pages/home'
import { MenubarButton } from '@/components/app/layout/menubar-button'
import { Button } from '@/components/ui/button'
import { Cloud, X } from 'lucide-react'
import { SpreadPageIndicator } from './components/spread-page-indicator'
import { UserAccountMenu } from './components/user-account-menu'
import { handleNavigateSpread } from './handlers/navigate-spread'
import { resolveMaxSpreadIndex } from './helpers/spread-index-bounds'
import {
  type SpreadPageIndicator as SpreadPageIndicatorValue,
  resolveSpreadPageLabels,
} from './helpers/spread-page-labels'
import { useSpreadIndexClamp } from './hooks/use-spread-index-clamp'

/** App top bar props: spread navigation and the user menu. The hamburger menu reads the editor contexts. */
export interface AppMenuBarProps {
  centerText?: string
  leftText?: string
  rightText?: string
  spreadIndex?: number
  onSpreadIndexChange?: (index: number) => void
  totalPages?: number
  /**
   * When set (including 0), clamps navigation to `[0, maxSpreadIndex]` instead of deriving from `totalPages`.
   * Use when spreads come from `/core2/spread/all` (duplicate page pairs, length ≠ floor(totalPages/2)).
   */
  maxSpreadIndex?: number
  /**
   * When set, drives the center page number badges instead of `2 * spreadIndex` math.
   */
  spreadPageIndicator?: SpreadPageIndicatorValue
  /** Disables the previous / next spread buttons, e.g. when a host app opened this one spread. */
  spreadNavigationLocked?: boolean
  pageViewMode?: PageViewMode
  userInitials?: string
  username?: string | null
  onLogout?: () => void
  onNavigate?: (direction: 'prev' | 'next') => void
  onClose?: () => void
}

const AppMenuBar: React.FC<AppMenuBarProps> = ({
  leftText = '',
  rightText = '',
  spreadIndex = 0,
  onSpreadIndexChange,
  totalPages = 9999,
  maxSpreadIndex: maxSpreadIndexProp,
  spreadPageIndicator,
  spreadNavigationLocked = false,
  pageViewMode = 'spread',
  userInitials,
  username,
  onLogout,
  onNavigate,
  onClose,
}) => {
  const safeTotalPages = Math.max(0, totalPages)
  const maxSpreadIndex = resolveMaxSpreadIndex(maxSpreadIndexProp, safeTotalPages)

  useSpreadIndexClamp({
    spreadIndex,
    maxSpreadIndex,
    maxSpreadIndexProp,
    safeTotalPages,
    onSpreadIndexChange,
  })

  const handleNavigate = (dir: 'prev' | 'next') => {
    if (spreadNavigationLocked) return
    handleNavigateSpread(dir, { spreadIndex, maxSpreadIndex, onSpreadIndexChange, onNavigate })
  }

  const pageLabels = resolveSpreadPageLabels({
    spreadIndex,
    safeTotalPages,
    pageViewMode,
    spreadPageIndicator,
  })

  return (
    <div
      role="navigation"
      aria-label="Main"
      data-app-menu-bar="true"
      className="bg-background border border-border flex items-center justify-between w-full h-[var(--menubar-height-mobile)] lg:h-[var(--menubar-height-desktop)] px-2 py-3"
    >
      {/* Left cluster */}
      <div className="flex items-center gap-1">
        <MenubarButton />
        <Cloud className="h-4 w-4 mx-3" aria-hidden="true" />
      </div>

      {/* Center cluster - Page Indicator */}
      <SpreadPageIndicator
        leftText={leftText}
        rightText={rightText}
        {...pageLabels}
        previousDisabled={spreadNavigationLocked || spreadIndex === 0}
        nextDisabled={spreadNavigationLocked || spreadIndex >= maxSpreadIndex}
        onPrevious={() => handleNavigate('prev')}
        onNext={() => handleNavigate('next')}
      />

      {/* Right cluster */}
      <div className="flex items-center gap-1">
        <UserAccountMenu userInitials={userInitials} username={username} onLogout={onLogout} />
        <Button
          variant="ghost"
          size="icon"
          className="h-[var(--menubar-button-size)] w-[var(--menubar-button-size)] active:bg-accent"
          aria-label="Close editor"
          onClick={() => onClose?.()}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export default React.memo(AppMenuBar)
